"use client";

import { getImageProps } from "next/image";
import { useEffect, useRef, useState } from "react";
import { Swiper, SwiperSlide } from "swiper/react";
import { Pagination, Autoplay, EffectFade } from "swiper/modules";
import { cn } from "@/lib/utils";

import "swiper/css";
import "swiper/css/pagination";
import "swiper/css/effect-fade";

type HeroItem = {
  asset: string;
  asset_mobile?: string;
  title: string;
  subtitle: string;
  textPosition?: "left" | "right";
  textDark?: boolean;
  textBottom?: boolean;
  textBottom_mobile?: boolean;
};

type Props = {
  heroes?: HeroItem[];
};

const DesignHero = ({ heroes = [] }: Props) => {
  const [isMobile, setIsMobile] = useState<boolean | null>(null);

  useEffect(() => {
    const mediaQuery = window.matchMedia("(max-width: 767px)");

    const update = () => setIsMobile(mediaQuery.matches);
    update();

    if (typeof mediaQuery.addEventListener === "function") {
      mediaQuery.addEventListener("change", update);
      return () => mediaQuery.removeEventListener("change", update);
    }

    mediaQuery.addListener(update);
    return () => mediaQuery.removeListener(update);
  }, []);

  const videoRef = useRef<HTMLVideoElement>(null);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          video.play();
          observer.disconnect();
        }
      },
      { threshold: 0.1 },
    );

    observer.observe(video);
    return () => observer.disconnect();
  }, []);

  let assets: HeroItem[] = [];
  if (heroes && Array.isArray(heroes)) {
    assets = heroes;
  }

  const isVideo = (url: string) =>
    /\.(mp4|webm|ogg)/i.test(url) ||
    url.includes("video") ||
    url.includes("mp4");

  return (
    <section className="bg-background-fixed relative h-[calc(100svh-100px)] md:h-[calc(100svh-120px)]">
      <style>{`
        @keyframes subtleZoomOut {
          0% {
            transform: scale(1.15) translateZ(0);
          }
          100% {
            transform: scale(1) translateZ(0);
          }
        }

        .hero-bg-media {
          transform: scale(1.15) translateZ(0);
          will-change: transform;
          backface-visibility: hidden;
        }
        
        .swiper-slide-active .hero-bg-media {
          animation: subtleZoomOut 7s cubic-bezier(0.25, 1, 0.5, 1) 0.1s forwards;
        }
        
        @keyframes fadeInUpText {
          from {
            opacity: 0;
            transform: translateY(40px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        .hero-animated-text {
          opacity: 0;
          transform: translateY(40px);
        }
        .swiper-slide-active .hero-animated-text {
          animation: fadeInUpText 0.8s cubic-bezier(0.2, 0.8, 0.2, 1) forwards;
        }
        .swiper-slide-active .hero-animated-text.delay-1 {
          animation-delay: 0.3s;
        }
        .swiper-slide-active .hero-animated-text.delay-2 {
          animation-delay: 0.5s;
        }

        .hero-slider-pagination {
          position: absolute;
          bottom: 40px !important;
          z-index: 50;
        }
        .hero-slider-pagination .swiper-pagination-bullet {
          width: 10px;
          height: 10px;
          background: rgba(0,0,0,0.3) !important;
          opacity: 1;
          transition: all 0.4s ease;
        }
        .hero-slider-pagination .swiper-pagination-bullet-active {
          background: #000 !important;
          width: 32px;
          border-radius: 8px;
        }
      `}</style>

      <div className="absolute w-full h-full top-0 start-0 design-hero-slider bg-white dark:bg-black">
        {assets.length > 0 && (
          <Swiper
            modules={[Pagination, Autoplay, EffectFade]}
            effect="fade"
            fadeEffect={{ crossFade: true }}
            rewind={assets.length > 1}
            speed={1200}
            autoplay={
              assets.length > 1
                ? { delay: 6000, disableOnInteraction: true }
                : false
            }
            pagination={
              assets.length > 1
                ? {
                    clickable: true,
                    el: ".hero-slider-pagination",
                  }
                : false
            }
            className="w-full h-full"
          >
            {assets.map((item, idx) => {
              const isVideoAsset = isVideo(item.asset);

              return (
                <SwiperSlide
                  key={idx}
                  className="w-full h-full overflow-hidden"
                >
                  <div className="absolute inset-0 w-full h-full">
                    {isVideoAsset ? (
                      <>
                        <video
                          ref={videoRef}
                          className="hero-bg-media absolute inset-0 w-full h-full object-cover"
                          src={
                            isMobile && item.asset_mobile
                              ? item.asset_mobile
                              : item.asset
                          }
                          poster={
                            isMobile
                              ? "/assets/imgs/poster-mobile.webp"
                              : "/assets/imgs/poster.webp"
                          }
                          autoPlay
                          loop
                          muted
                          playsInline
                          preload="metadata"
                        />
                      </>
                    ) : (
                      (() => {
                        const imageMobile = getImageProps({
                          src: item.asset_mobile || item.asset,
                          alt: `Hero asset ${idx + 1}`,
                          width: 900,
                          height: 1600,
                          sizes: "(max-width: 767px) 100vw, 0px",
                          className:
                            "hero-bg-media absolute inset-0 w-full h-full object-cover",
                          style: { objectFit: "cover" },
                          priority: idx === 0,
                        });
                        const imageDesktop = getImageProps({
                          src: item.asset,
                          alt: `Hero asset ${idx + 1}`,
                          width: 1920,
                          height: 1080,
                          sizes: "(min-width: 768px) 100vw, 100vw",
                          className:
                            "hero-bg-media absolute inset-0 w-full h-full object-cover",
                          style: { objectFit: "cover" },
                          priority: idx === 0,
                        });

                        return (
                          <img
                            {...(isMobile
                              ? imageMobile.props
                              : imageDesktop.props)}
                          />
                        );
                      })()
                    )}
                    <div className="absolute w-full h-full top-0 start-0 opacity-80 mix-blend-multiply bg-black/10"></div>
                  </div>

                  <div className="absolute inset-0 w-full h-full pointer-events-none">
                    <div className="inner-container h-full relative z-10">
                      <div
                        className={cn(
                          "h-full flex",
                          item.textBottom_mobile
                            ? "items-end pb-10"
                            : "items-center",
                          item.textBottom
                            ? "md:items-end md:pb-10"
                            : "md:items-center md:pb-0",
                        )}
                      >
                        <div
                          className={`title-wrapper pointer-events-auto relative ${(item.textPosition || "right") === "right" ? "md:ml-[50%]" : ""}`}
                        >
                          {item.title && (
                            <h1
                              className={cn(
                                "hero-animated-text delay-1 text-[45px] sm:text-[50px] md:text-[55px] xl:text-[60px] 2xl:text-[65px] leading-[1] md:leading-[1.25] tracking-widest font-superclarendon font-light",
                                item.textDark ? "text-black" : "text-white",
                              )}
                              dangerouslySetInnerHTML={{ __html: item.title }}
                            />
                          )}
                          {item.subtitle && (
                            <h1
                              className={cn(
                                "hero-animated-text delay-2 text-[45px] sm:text-[50px] md:text-[55px] xl:text-[60px] 2xl:text-[65px] leading-[1] md:leading-[1.25] tracking-widest font-superclarendon font-semibold italic mt-1",
                                item.textDark ? "text-black" : "text-white",
                              )}
                              dangerouslySetInnerHTML={{
                                __html: item.subtitle,
                              }}
                            />
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </SwiperSlide>
              );
            })}

            <div className="hero-slider-pagination flex justify-center gap-[6px] w-full left-0 pointer-events-auto"></div>
          </Swiper>
        )}
      </div>
    </section>
  );
};

export default DesignHero;
